const SUGGESTION_SCOPE_PATTERN = /^\[([^\]]+)\]\s*(.+)$/;
const SUGGESTION_TEMPLATE_PATTERN = /\{\{\s*([^}]+?)\s*\}\}/g;

const emptyParsedSuggestion = (label = '') => ({
  scope: '*',
  includes: ['*'],
  excludes: [],
  label,
  is_wildcard: true,
});

/**
 * Split a scope prefix into include / exclude route names.
 * Tokens starting with "!" are exclusions. "*" is a wildcard include.
 *
 * @param {string} scope_raw
 * @returns {{ includes: string[], excludes: string[] }}
 */
const parseScopeTokens = (scope_raw) => {
  const tokens = String(scope_raw)
    .split(',')
    .map((token) => token.trim())
    .filter(Boolean);
  const includes = [];
  const excludes = [];

  tokens.forEach((token) => {
    if (token.startsWith('!')) {
      const name = token.slice(1).trim();

      if (name) {
        excludes.push(name);
      }

      return;
    }

    includes.push(token);
  });

  return { includes, excludes };
};

/**
 * Parse a suggested question string with an optional scope prefix.
 * Examples:
 * - "[*] explain what you can do"
 * - "[AccountPage] Analyze this account"
 * - "[!AccountPage] Global tip"
 * - "[*, !AccountPage] Everywhere except AccountPage"
 * Invalid / missing prefix → treated as wildcard with the full trimmed string as label.
 *
 * @param {string} raw
 * @returns {{
 *   scope: string,
 *   includes: string[],
 *   excludes: string[],
 *   label: string,
 *   is_wildcard: boolean,
 * }}
 */
export const parseSuggestedQuestion = (raw) => {
  const trimmed = typeof raw === 'string' ? raw.trim() : '';

  if (!trimmed) {
    return emptyParsedSuggestion('');
  }

  const match = trimmed.match(SUGGESTION_SCOPE_PATTERN);

  if (!match) {
    return emptyParsedSuggestion(trimmed);
  }

  const scope = match[1].trim();
  const label = match[2].trim();
  const { includes, excludes } = parseScopeTokens(scope);

  if ((!includes.length && !excludes.length) || !label) {
    return emptyParsedSuggestion(trimmed);
  }

  const is_wildcard = includes.includes('*') || includes.length === 0;

  return {
    scope,
    includes: is_wildcard && !includes.includes('*') ? ['*', ...includes] : includes,
    excludes,
    label,
    is_wildcard,
  };
};

const getMatchedRouteNames = (route) => (route?.matched || [])
  .map((record) => record?.name)
  .filter(Boolean);

const scopeMatchesRoute = (scope, matched_names) => {
  if (scope === '*') {
    return true;
  }

  return matched_names.includes(scope);
};

/**
 * Whether a parsed suggestion should be shown for the current route.
 * Includes match if "*" or equals the current route name or any parent in matched.
 * Excludes (prefix "!") hide the suggestion when that route (or parent) matches.
 * Exclusion-only prefixes (e.g. "[!AccountPage]") apply on every other page.
 *
 * @param {{
 *   includes?: string[],
 *   excludes?: string[],
 *   scope?: string,
 *   is_wildcard?: boolean,
 * }} parsed
 * @param {{ name?: string|null, matched?: Array<{ name?: string|null }> }} route
 * @returns {boolean}
 */
export const suggestionMatchesRoute = (parsed, route) => {
  if (!parsed) {
    return false;
  }

  const matched_names = getMatchedRouteNames(route);
  const excludes = parsed.excludes || [];
  const includes = parsed.includes || [];

  if (excludes.some((scope) => scopeMatchesRoute(scope, matched_names))) {
    return false;
  }

  if (parsed.is_wildcard || includes.includes('*') || includes.length === 0) {
    return true;
  }

  return includes.some((scope) => scopeMatchesRoute(scope, matched_names));
};

const lookupTemplateVariable = (variables, key) => {
  const trimmed_key = String(key || '').trim();

  if (!trimmed_key || !variables || typeof variables !== 'object') {
    return undefined;
  }

  if (Object.prototype.hasOwnProperty.call(variables, trimmed_key)) {
    return variables[trimmed_key];
  }

  const matched_key = Object.keys(variables).find(
    (name) => name.toLowerCase() === trimmed_key.toLowerCase()
  );

  if (matched_key === undefined) {
    return undefined;
  }

  return variables[matched_key];
};

/**
 * Replace {{variable}} tokens in a suggestion label.
 * A supported variable with no value marks the result as incomplete, so the caller can
 * hide the suggestion rather than show a sentence with a hole in it.
 * Unsupported names are left untouched, so an upstream typo stays visible.
 *
 * @param {string} label
 * @param {Record<string, string|number|null|undefined>} [variables]
 * @returns {{ label: string, is_complete: boolean }}
 */
export const applySuggestionTemplate = (label, variables = {}) => {
  if (typeof label !== 'string' || !label) {
    return {
      label: '',
      is_complete: false,
    };
  }

  let is_complete = true;
  const interpolated = label.replace(
    new RegExp(SUGGESTION_TEMPLATE_PATTERN.source, 'g'),
    (match, key) => {
      const value = lookupTemplateVariable(variables, key);

      if (value === undefined) {
        return match;
      }

      const text = value === null ? '' : String(value).trim();

      if (!text) {
        is_complete = false;
      }

      return text;
    }
  );

  return {
    label: interpolated.trim(),
    is_complete,
  };
};

/**
 * How specific a scope is for the current route: "*" is the least specific,
 * a leaf route name the most specific. Returns -1 when the scope does not apply.
 *
 * @param {string} scope
 * @param {string[]} matched_names
 * @returns {number}
 */
const getScopeSpecificity = (scope, matched_names) => {
  if (scope === '*') {
    return 0;
  }

  const index = matched_names.indexOf(scope);

  return index === -1 ? -1 : index + 1;
};

/**
 * Split a multi-line opening statement into scoped variants, one per prefixed line.
 * Lines without a prefix are appended to the previous variant, so a single
 * multi-line (markdown) opener keeps working as before.
 *
 * @param {string} raw
 * @returns {Array<{ includes: string[], excludes: string[], label: string }>}
 */
const parseOpeningStatementVariants = (raw) => {
  const variants = [];

  String(raw).split('\n').forEach((line) => {
    if (SUGGESTION_SCOPE_PATTERN.test(line.trim())) {
      variants.push(parseSuggestedQuestion(line));

      return;
    }

    const previous = variants[variants.length - 1];

    if (previous) {
      previous.label = `${previous.label}\n${line}`;

      return;
    }

    if (line.trim()) {
      variants.push(emptyParsedSuggestion(line.trim()));
    }
  });

  return variants;
};

/**
 * Resolve the opening statement for the current route.
 * Each line may carry a scope prefix ("[*]", "[AccountPage]", "[*, !AccountPage]");
 * the most specific matching variant wins, ties going to the first declared line.
 * Variants with unresolved {{variables}} are skipped in favor of a broader one.
 *
 * @param {string} raw
 * @param {{ name?: string|null, matched?: Array<{ name?: string|null }> }} route
 * @param {Record<string, string|number|null|undefined>} [variables]
 * @returns {string}
 */
export const pickOpeningStatement = (raw, route, variables = {}) => {
  const trimmed = typeof raw === 'string' ? raw.trim() : '';

  if (!trimmed) {
    return '';
  }

  const matched_names = getMatchedRouteNames(route);
  const resolved = parseOpeningStatementVariants(trimmed)
    .map((variant, index) => ({
      variant,
      index,
      specificity: Math.max(
        ...(variant.includes.length ? variant.includes : ['*'])
          .map((scope) => getScopeSpecificity(scope, matched_names))
      ),
    }))
    .filter(({ variant }) => variant.label && suggestionMatchesRoute(variant, route))
    .sort((a, b) => (b.specificity - a.specificity) || (a.index - b.index))
    .map(({ variant }) => applySuggestionTemplate(variant.label, variables))
    .find((templated) => templated.is_complete && templated.label);

  return resolved ? resolved.label : '';
};

/**
 * Leaf route name used as the chatbot current_page variable.
 *
 * @param {{ name?: string|null }} route
 * @returns {string}
 */
export const getCurrentPageName = (route) => {
  if (typeof route?.name === 'string' && route.name) {
    return route.name;
  }

  return '';
};

const ACCOUNT_PAGE_ROUTE_NAME = 'AccountPage';

/**
 * Account id for the chatbot conversation variable.
 * Returns the route param when on AccountPage or any of its children, otherwise 0.
 *
 * @param {{
 *   params?: { account_id?: string|number },
 *   matched?: Array<{ name?: string|null }>
 * }} route
 * @returns {number}
 */
export const getAccountIdFromRoute = (route) => {
  const is_account_route = (route?.matched || []).some(
    (record) => record?.name === ACCOUNT_PAGE_ROUTE_NAME
  );

  if (!is_account_route) {
    return 0;
  }

  const account_id = Number(route?.params?.account_id);

  if (!Number.isFinite(account_id) || account_id <= 0) {
    return 0;
  }

  return account_id;
};

/**
 * Normalize Dify retriever_resources (citations) from stream or history.
 * Live answers: message_end.metadata.retriever_resources
 * History: message.retriever_resources from GET /messages
 *
 * @param {Array<object>|null|undefined} resources
 * @returns {Array<{
 *   id: string,
 *   position: number,
 *   document_name: string,
 *   dataset_name: string,
 *   content: string,
 *   score: number|null,
 * }>}
 */
export const normalizeRetrieverResources = (resources) => {
  if (!Array.isArray(resources)) {
    return [];
  }

  return resources
    .map((item, index) => {
      if (!item || typeof item !== 'object') {
        return null;
      }

      const document_name = typeof item.document_name === 'string'
        ? item.document_name.trim()
        : '';
      const dataset_name = typeof item.dataset_name === 'string'
        ? item.dataset_name.trim()
        : '';
      const content = typeof item.content === 'string'
        ? item.content.trim()
        : (typeof item.summary === 'string' ? item.summary.trim() : '');
      const position = Number.isFinite(Number(item.position))
        ? Number(item.position)
        : index + 1;
      const score = Number.isFinite(Number(item.score))
        ? Number(item.score)
        : null;
      const id = item.segment_id
        || item.document_id
        || item.id
        || `${document_name}-${position}`;

      return {
        id: String(id),
        position,
        document_name,
        dataset_name,
        content,
        score,
      };
    })
    .filter((item) => item && (item.document_name || item.content));
};

export const IGNORED_WORKFLOW_NODE_TYPES = [
  'assigner',
  'code',
  'if-else',
  'variable-aggregator',
];

const WORKFLOW_STATUS_SUFFIX_PATTERN = /\s*\[([^\]]*)\]\s*$/;

/**
 * Whether a workflow node type should update the chatbot typing status.
 *
 * @param {string|null|undefined} node_type
 * @returns {boolean}
 */
export const shouldDisplayWorkflowNodeStatus = (node_type) => {
  if (typeof node_type !== 'string' || !node_type) {
    return true;
  }

  return !IGNORED_WORKFLOW_NODE_TYPES.includes(node_type);
};

/**
 * Parse a Dify workflow node title into a user-facing status label.
 * Trailing "[Friendly message]" suffix wins; empty/missing suffix → base title.
 * Examples:
 * - "Agentic helper [Je réfléchis]" → "Je réfléchis"
 * - "Agentic helper" or "Agentic helper []" → "Agentic helper"
 *
 * @param {string} title
 * @returns {string}
 */
export const parseWorkflowNodeStatusLabel = (title) => {
  const trimmed = typeof title === 'string' ? title.trim() : '';

  if (!trimmed) {
    return '';
  }

  const match = trimmed.match(WORKFLOW_STATUS_SUFFIX_PATTERN);

  if (!match) {
    return trimmed;
  }

  const friendly_label = match[1].trim();

  if (friendly_label) {
    return friendly_label;
  }

  return trimmed.slice(0, match.index).trim() || trimmed;
};
